import type {
  AdminAnalyticsUsersResponse,
  AdminAnalyticsUsersRequest,
  AdminAnalyticsGatewayRequest,
  AdminAnalyticsGatewayResponse,
  AdminCreateCheckoutRequest,
  AdminCreateCheckoutResponse,
  AdminBalanceAdjustRequest,
  AdminErrorLogsResponse,
  AdminModelsMonitoringResponse,
  AdminModelsMonitoringQuery,
  AdminModelClickEventsResponse,
  AdminPaymentProvidersResponse,
  AdminSubscriptionPlansResponse,
  AdminGrantSubscriptionRequest,
  AdminGrantSubscriptionResponse,
  AiInfluencer,
  AdminUpdateSubscriptionPlanRequest,
  AdminSettingsUpdate,
  AdminStatsResponse,
  AdminTopModelsResponse,
  AdminUserAnalyticsResponse,
  AdminUserAnalyticsSummaryResponse,
  AdminUserDevicesResponse,
  AdminUserErrorLogsResponse,
  AdminUserIpAddressesResponse,
  AdminUserPageViewsResponse,
  AdminUserSessionEventsResponse,
  AIModel,
  AnalyticsQueryTableRequest,
  AnalyticsQueryTableResponse,
  ApiErrorBody,
  Attachment,
  AuthResponse,
  ChatOverview,
  ChatPref,
  ChatPrefPatch,
  ChatSearchResult,
  ChatUser,
  Conversation,
  ConversationListResponse,
  ConversationWithMessages,
  DirectMessage,
  Message,
  ForwardBatchRequest,
  ForwardBatchResponse,
  GroupChat,
  CreateSnapshotResponse,
  CreateConversationRequest,
  CreateAiInfluencerRequest,
  CreateCharacterRequest,
  CreateFolderRequest,
  CreateJobRequest,
  CreateProjectRequest,
  CreateSoulCastRequest,
  CreateSoulCastResponse,
  CreateSoulIdentityRequest,
  JobLikenessRating,
  SoulCastEstimateRequest,
  SoulCastEstimateResponse,
  SoulPhotoCheckResponse,
  EstimateRequest,
  EstimateResponse,
  Folder,
  Job,
  JobListResponse,
  JobMediaResponse,
  VoiceSpeakRequest,
  VoiceSpeakResponse,
  VoiceTranscribeRequest,
  VoiceTranscribeResponse,
  JobAttachmentResponse,
  ListAiInfluencersResponse,
  ListCharactersResponse,
  ListSoulIdentitiesResponse,
  MeResponse,
  MessageByRequestResponse,
  ModelPrice,
  ModelsResponse,
  PersonalizationSettings,
  PublicSnapshot,
  PublicSnapshotResponse,
  Project,
  ProjectListResponse,
  ReportErrorRequest,
  Section,
  SendMessageRequest,
  SoulIdentity,
  Character,
  TrackBatchRequest,
  TrackDeviceRequest,
  TrackModelClickRequest,
  TrackPageViewRequest,
  TrackSectionClickRequest,
  TrackSessionEventRequest,
  TopupResponse,
  TransactionListResponse,
  UpdateAiInfluencerRequest,
  UpdateCharacterRequest,
  UpdateConversationRequest,
  UpdateFolderRequest,
  UpdatePersonalizationRequest,
} from '@volt/shared';
import type { AdminSettingsResponse, AdminUsersResponse } from './types.js';
import { XHRChatStream } from '../lib/sse.js';

export interface Tokens {
  accessToken: string;
  refreshToken: string;
}

const TOKEN_SCHEME = 'Bearer';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly requestId?: string | null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type TokensListener = (tokens: Tokens | null) => void;
type AuthUpdateHandler = (auth: AuthResponse) => void;

/**
 * Typed API client. Attaches the bearer access token, transparently refreshes it
 * once on a 401 and retries the original request, and surfaces `{ error }`
 * bodies as {@link ApiError}. The chat endpoint is exposed as a raw SSE stream.
 */
export interface DirectMessagePage {
  messages: DirectMessage[];
  hasMore?: boolean;
  peer?: { online: boolean; typing: boolean };
  typingIds?: string[];
  pinned?: DirectMessage[];
}

export interface DirectChatEvent {
  type: 'chat' | 'typing';
  kind: 'direct' | 'group';
  targetId: string;
}

function pageQueryString(page?: { limit?: number; before?: string }): string {
  const params = new URLSearchParams();
  if (page?.limit) params.set('limit', String(page.limit));
  if (page?.before) params.set('before', page.before);
  const query = params.toString();
  return query ? `?${query}` : '';
}

export class ApiClient {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private refreshing: Promise<boolean> | null = null;
  private readonly tokenListeners = new Set<TokensListener>();
  private authUpdateHandler: AuthUpdateHandler | null = null;

  constructor(private readonly baseUrl: string) {}

  get isAuthenticated(): boolean {
    return this.accessToken !== null;
  }

  setTokens(tokens: Tokens | null): void {
    this.accessToken = tokens?.accessToken ?? null;
    this.refreshToken = tokens?.refreshToken ?? null;
    for (const listener of this.tokenListeners) listener(tokens);
  }

  onTokens(listener: TokensListener): () => void {
    this.tokenListeners.add(listener);
    return () => this.tokenListeners.delete(listener);
  }

  setAuthUpdateHandler(handler: AuthUpdateHandler | null): void {
    this.authUpdateHandler = handler;
  }

  // ── Auth ─────────────────────────────────────────────────────────────
  authTelegram(initData: string): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({ initData }),
      auth: false,
    });
  }

  async refresh(): Promise<boolean> {
    if (!this.refreshToken) return false;
    if (!this.refreshing) {
      this.refreshing = this.doRefresh().finally(() => {
        this.refreshing = null;
      });
    }
    return this.refreshing;
  }

  private async doRefresh(): Promise<boolean> {
    if (!this.refreshToken) return false;
    try {
      const response = await fetch(this.url('/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });
      if (!response.ok) {
        this.setTokens(null);
        return false;
      }
      const auth = (await response.json()) as AuthResponse;
      this.setTokens({ accessToken: auth.accessToken, refreshToken: auth.refreshToken });
      this.authUpdateHandler?.(auth);
      return true;
    } catch (error) {
      console.warn('Token refresh failed', error);
      return false;
    }
  }

  // ── Account / profile ───────────────────────────────────────────────
  getMe(): Promise<MeResponse> {
    return this.request<MeResponse>('/me');
  }

  listDirectChatFriends(): Promise<{ friends: ChatUser[]; publicId: number }> {
    return this.request<{ friends: ChatUser[]; publicId: number }>('/direct-chat/friends');
  }

  getDirectChatOverview(): Promise<ChatOverview> {
    return this.request<ChatOverview>('/direct-chat/summaries');
  }

  searchDirectChats(query: string): Promise<{ results: ChatSearchResult[] }> {
    return this.request<{ results: ChatSearchResult[] }>(
      `/direct-chat/search?q=${encodeURIComponent(query)}`,
    );
  }

  updateDirectChatPref(
    kind: 'direct' | 'group',
    id: string,
    patch: ChatPrefPatch,
  ): Promise<{ pref: ChatPref }> {
    return this.request<{ pref: ChatPref }>(
      `/direct-chat/chats/${kind}/${encodeURIComponent(id)}/prefs`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    );
  }

  sendDirectChatTyping(kind: 'direct' | 'group', id: string): Promise<void> {
    return this.requestVoid(`/direct-chat/chats/${kind}/${encodeURIComponent(id)}/typing`, {
      method: 'POST',
    });
  }

  setDirectChatReaction(
    kind: 'direct' | 'group',
    id: string,
    messageId: string,
    emoji: string | null,
  ): Promise<void> {
    return this.requestVoid(
      `/direct-chat/conversations/${kind}/${encodeURIComponent(id)}/messages/${encodeURIComponent(messageId)}/reaction`,
      { method: 'PUT', body: JSON.stringify({ emoji }) },
    );
  }

  setDirectChatPin(
    kind: 'direct' | 'group',
    id: string,
    messageId: string,
    pinned: boolean,
  ): Promise<void> {
    return this.requestVoid(
      `/direct-chat/conversations/${kind}/${encodeURIComponent(id)}/messages/${encodeURIComponent(messageId)}/pin`,
      { method: 'PUT', body: JSON.stringify({ pinned }) },
    );
  }

  getDirectChatPushConfig(): Promise<{ enabled: boolean; publicKey: string }> {
    return this.request<{ enabled: boolean; publicKey: string }>('/direct-chat/push/config');
  }

  subscribeDirectChatPush(subscription: PushSubscriptionJSON): Promise<void> {
    return this.requestVoid('/direct-chat/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(subscription),
    });
  }

  unsubscribeDirectChatPush(endpoint: string): Promise<void> {
    return this.requestVoid('/direct-chat/push/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    });
  }

  /** Opens the chat event stream and reconnects with backoff; returns a stop function. */
  subscribeDirectChatEvents(
    onEvent: (event: DirectChatEvent) => void,
    onState?: (live: boolean) => void,
  ): () => void {
    const controller = new AbortController();
    const { signal } = controller;
    const run = async () => {
      let delay = 1_000;
      while (!signal.aborted) {
        try {
          const response = await fetch(this.url('/direct-chat/events'), {
            headers: { ...this.authHeaders(true, false), Accept: 'text/event-stream' },
            signal,
          });
          if (response.status === 401 && (await this.refresh())) continue;
          if (!response.ok || !response.body) throw new Error('events unavailable');
          onState?.(true);
          delay = 1_000;
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let end = buffer.indexOf('\n\n');
            while (end !== -1) {
              const block = buffer.slice(0, end);
              buffer = buffer.slice(end + 2);
              const data = /^data: (.*)$/m.exec(block)?.[1];
              if (data && data !== '{}') {
                try {
                  onEvent(JSON.parse(data) as DirectChatEvent);
                } catch {
                  // Malformed events are ignored.
                }
              }
              end = buffer.indexOf('\n\n');
            }
          }
        } catch {
          if (signal.aborted) return;
        }
        onState?.(false);
        await new Promise((resolve) => window.setTimeout(resolve, delay));
        delay = Math.min(delay * 2, 30_000);
      }
    };
    void run();
    return () => controller.abort();
  }

  deleteDirectChat(kind: 'direct' | 'group', id: string, scope: 'me' | 'everyone'): Promise<void> {
    return this.requestVoid(`/direct-chat/chats/${kind}/${encodeURIComponent(id)}?scope=${scope}`, {
      method: 'DELETE',
    });
  }

  setDirectChatBlocked(friendId: string, blocked: boolean): Promise<void> {
    return this.requestVoid(`/direct-chat/blocks/${encodeURIComponent(friendId)}`, {
      method: blocked ? 'PUT' : 'DELETE',
    });
  }

  addDirectChatFriend(identifier: string): Promise<{ friend: ChatUser }> {
    return this.request<{ friend: ChatUser }>('/direct-chat/friends', {
      method: 'POST',
      body: JSON.stringify({ identifier }),
    });
  }

  listDirectMessages(
    friendId: string,
    page?: { limit?: number; before?: string },
  ): Promise<DirectMessagePage> {
    return this.request<DirectMessagePage>(
      `/direct-chat/friends/${encodeURIComponent(friendId)}/messages${pageQueryString(page)}`,
    );
  }

  sendDirectMessage(
    friendId: string,
    content: string,
    clientRequestId: string,
    replyToMessageId?: string,
    attachmentIds?: string[],
  ): Promise<{ message: DirectMessage }> {
    return this.request<{ message: DirectMessage }>(
      `/direct-chat/friends/${encodeURIComponent(friendId)}/messages`,
      {
        method: 'POST',
        body: JSON.stringify({ content, clientRequestId, replyToMessageId, attachmentIds }),
      },
    );
  }

  /** `preview` asks for the small inline version of an image (the original otherwise). */
  async getDirectChatAttachmentUrl(
    attachmentId: string,
    variant: 'original' | 'preview' = 'original',
  ): Promise<{ url: string }> {
    const { url } = await this.request<{ url: string }>(
      `/direct-chat/attachments/${encodeURIComponent(attachmentId)}/url?variant=${variant}`,
    );
    return { url: this.mediaUrl(url) };
  }

  listDirectChatGroups(): Promise<{ groups: GroupChat[] }> {
    return this.request<{ groups: GroupChat[] }>('/direct-chat/groups');
  }

  createDirectChatGroup(title: string, memberIds: string[]): Promise<{ group: GroupChat }> {
    return this.request<{ group: GroupChat }>('/direct-chat/groups', {
      method: 'POST',
      body: JSON.stringify({ title, memberIds }),
    });
  }

  listGroupMessages(
    groupId: string,
    page?: { limit?: number; before?: string },
  ): Promise<DirectMessagePage> {
    return this.request<DirectMessagePage>(
      `/direct-chat/groups/${encodeURIComponent(groupId)}/messages${pageQueryString(page)}`,
    );
  }

  sendGroupMessage(
    groupId: string,
    content: string,
    clientRequestId: string,
    replyToMessageId?: string,
    attachmentIds?: string[],
  ): Promise<{ message: DirectMessage }> {
    return this.request<{ message: DirectMessage }>(
      `/direct-chat/groups/${encodeURIComponent(groupId)}/messages`,
      {
        method: 'POST',
        body: JSON.stringify({ content, clientRequestId, replyToMessageId, attachmentIds }),
      },
    );
  }

  deleteDirectMessage(
    destination: { friendId: string } | { groupId: string },
    messageId: string,
    options?: { forEveryone?: boolean },
  ): Promise<void> {
    const kind = 'friendId' in destination ? 'direct' : 'group';
    const id = 'friendId' in destination ? destination.friendId : destination.groupId;
    const query = options?.forEveryone ? '?mode=everyone' : '';
    return this.requestVoid(
      `/direct-chat/conversations/${kind}/${encodeURIComponent(id)}/messages/${encodeURIComponent(messageId)}${query}`,
      {
        method: 'DELETE',
      },
    );
  }

  editDirectMessage(
    destination: { friendId: string } | { groupId: string },
    messageId: string,
    content: string,
  ): Promise<{ message: DirectMessage }> {
    const kind = 'friendId' in destination ? 'direct' : 'group';
    const id = 'friendId' in destination ? destination.friendId : destination.groupId;
    return this.request<{ message: DirectMessage }>(
      `/direct-chat/conversations/${kind}/${encodeURIComponent(id)}/messages/${encodeURIComponent(messageId)}`,
      { method: 'PATCH', body: JSON.stringify({ content }) },
    );
  }

  rewindConversation(conversationId: string, messageId: string): Promise<{ ok: true }> {
    return this.request<{ ok: true }>(
      `/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/rewind`,
      { method: 'POST' },
    );
  }

  setMessageChapter(
    conversationId: string,
    messageId: string,
    chapter: boolean,
  ): Promise<{ message: Message }> {
    return this.request<{ message: Message }>(
      `/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/chapter`,
      { method: 'POST', body: JSON.stringify({ chapter }) },
    );
  }

  editConversationMessage(
    conversationId: string,
    messageId: string,
    content: string,
  ): Promise<{ message: Message }> {
    return this.request<{ message: Message }>(
      `/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}`,
      { method: 'PATCH', body: JSON.stringify({ content }) },
    );
  }

  forwardChatMessages(body: ForwardBatchRequest): Promise<ForwardBatchResponse> {
    return this.request<ForwardBatchResponse>('/direct-chat/forward-batch', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  replyDirectChatAi(
    destination: { friendId: string } | { groupId: string },
    promptMessageId: string,
    modelId: string,
    clientRequestId: string = crypto.randomUUID(),
    signal?: AbortSignal,
  ): Promise<{ message: DirectMessage }> {
    const kind = 'friendId' in destination ? 'direct' : 'group';
    const id = 'friendId' in destination ? destination.friendId : destination.groupId;
    return this.request<{ message: DirectMessage }>(
      `/direct-chat/conversations/${kind}/${encodeURIComponent(id)}/reply-ai`,
      {
        method: 'POST',
        body: JSON.stringify({
          promptMessageId,
          modelId,
          clientRequestId,
        }),
        signal,
      },
    );
  }

  shareAiResponse(
    destination: { friendId: string } | { groupId: string },
    conversationId: string,
    messageId: string,
    clientRequestId: string = crypto.randomUUID(),
  ): Promise<{ message: DirectMessage }> {
    const kind = 'friendId' in destination ? 'direct' : 'group';
    const id = 'friendId' in destination ? destination.friendId : destination.groupId;
    return this.request<{ message: DirectMessage }>(
      `/direct-chat/conversations/${kind}/${encodeURIComponent(id)}/share-ai`,
      {
        method: 'POST',
        body: JSON.stringify({
          conversationId,
          messageId,
          clientRequestId,
        }),
      },
    );
  }

  forwardDirectMessage(
    destination: { friendId: string } | { groupId: string },
    source: { friendId: string } | { groupId: string },
    messageId: string,
    clientRequestId: string,
  ): Promise<{ message: DirectMessage }> {
    const kind = 'friendId' in destination ? 'direct' : 'group';
    const id = 'friendId' in destination ? destination.friendId : destination.groupId;
    return this.request<{ message: DirectMessage }>(
      `/direct-chat/conversations/${kind}/${encodeURIComponent(id)}/forward`,
      {
        method: 'POST',
        body: JSON.stringify({
          sourceKind: 'friendId' in source ? 'direct' : 'group',
          sourceId: 'friendId' in source ? source.friendId : source.groupId,
          messageId,
          clientRequestId,
        }),
      },
    );
  }

  markDirectChatReceipt(
    destination: { friendId: string } | { groupId: string },
    messageId: string,
    status: 'delivered' | 'read',
  ): Promise<{ message: DirectMessage }> {
    const kind = 'friendId' in destination ? 'direct' : 'group';
    const id = 'friendId' in destination ? destination.friendId : destination.groupId;
    return this.request<{ message: DirectMessage }>(
      `/direct-chat/conversations/${kind}/${encodeURIComponent(id)}/messages/${encodeURIComponent(messageId)}/receipt`,
      { method: 'POST', body: JSON.stringify({ status }) },
    );
  }

  getPersonalization(): Promise<{ personalization: PersonalizationSettings }> {
    return this.request<{ personalization: PersonalizationSettings }>('/me/personalization');
  }

  updatePersonalization(
    patch: UpdatePersonalizationRequest,
  ): Promise<{ personalization: PersonalizationSettings }> {
    return this.request<{ personalization: PersonalizationSettings }>('/me/personalization', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  getModels(): Promise<ModelsResponse> {
    return this.request<ModelsResponse>('/models');
  }

  exportData(): Promise<unknown> {
    return this.request<unknown>('/account/export', { method: 'POST' });
  }

  deleteAccount(): Promise<void> {
    return this.requestVoid('/account', { method: 'DELETE' });
  }

  // ── Chat ─────────────────────────────────────────────────────────────
  estimate(body: EstimateRequest, signal?: AbortSignal): Promise<EstimateResponse> {
    return this.request<EstimateResponse>('/chat/estimate', {
      method: 'POST',
      body: JSON.stringify(body),
      signal,
    });
  }

  /**
   * Open the chat SSE stream. Uses `XMLHttpRequest` (via {@link XHRChatStream})
   * rather than `fetch` + `ReadableStream`: Safari/WebKit — including the
   * Telegram in-app browser the Mini App runs in on iOS — does not reliably
   * deliver incremental fetch response chunks, which surfaced as
   * `TypeError: Load failed` mid-stream (the reply would only "appear" after
   * leaving and reopening the chat, once the whole response had buffered).
   */
  async openChatStream(body: SendMessageRequest, signal?: AbortSignal): Promise<XHRChatStream> {
    let stream = this.rawXhrStream(body, signal);
    let status = await stream.status;
    if (status === 401 && (await this.refresh())) {
      stream = this.rawXhrStream(body, signal);
      status = await stream.status;
    }
    if (status < 200 || status >= 300) {
      const text = await stream.text;
      throw this.errorFromText(status, text);
    }
    return stream;
  }

  stopGeneration(assistantMessageId: string): Promise<void> {
    return this.requestVoid(`/chat/messages/${encodeURIComponent(assistantMessageId)}/stop`, {
      method: 'POST',
    });
  }

  getMessageByRequest(clientRequestId: string): Promise<MessageByRequestResponse> {
    return this.request<MessageByRequestResponse>(
      `/chat/messages/by-request/${encodeURIComponent(clientRequestId)}`,
    );
  }

  uploadAttachment(body: {
    fileName: string;
    mime: string;
    dataBase64: string;
    messageId?: string | null;
  }): Promise<{ attachment: Attachment; deduplicated: boolean }> {
    return this.request<{ attachment: Attachment; deduplicated: boolean }>('/attachments', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getAttachmentUrl(
    attachmentId: string,
    variant: 'original' | 'preview' = 'original',
  ): Promise<{ url: string }> {
    const { url } = await this.request<{ url: string }>(
      `/attachments/${encodeURIComponent(attachmentId)}/url?variant=${variant}`,
    );
    return { url: this.mediaUrl(url) };
  }

  // ── Conversations ───────────────────────────────────────────────────
  listConversations(
    params: {
      section?: Section;
      search?: string;
      archived?: boolean;
    } = {},
  ): Promise<ConversationListResponse> {
    const query = new URLSearchParams();
    if (params.section) query.set('section', params.section);
    if (params.search) query.set('search', params.search);
    // The API's query schema (see apps/api/src/routes/conversations.ts) expects
    // `includeArchived`, not `archived` — the previous name mismatch meant the
    // archive filter was silently ignored (zod drops unknown keys), so "Show
    // archived" never actually returned archived conversations.
    if (params.archived !== undefined) query.set('includeArchived', String(params.archived));
    const suffix = query.toString();
    return this.request<ConversationListResponse>(`/conversations${suffix ? `?${suffix}` : ''}`);
  }

  createConversation(body: CreateConversationRequest): Promise<Conversation> {
    return this.request<Conversation>('/conversations', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  getConversation(id: string): Promise<ConversationWithMessages> {
    return this.request<ConversationWithMessages>(`/conversations/${encodeURIComponent(id)}`);
  }

  updateConversation(id: string, patch: UpdateConversationRequest): Promise<Conversation> {
    return this.request<Conversation>(`/conversations/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  deleteConversation(id: string): Promise<void> {
    return this.requestVoid(`/conversations/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  createShare(conversationId: string): Promise<CreateSnapshotResponse> {
    return this.request<CreateSnapshotResponse>(
      `/conversations/${encodeURIComponent(conversationId)}/share`,
      { method: 'POST' },
    );
  }

  getShare(conversationId: string): Promise<{ snapshot: PublicSnapshot | null }> {
    return this.request<{ snapshot: PublicSnapshot | null }>(
      `/conversations/${encodeURIComponent(conversationId)}/share`,
    );
  }

  revokeShare(conversationId: string): Promise<void> {
    return this.requestVoid(`/conversations/${encodeURIComponent(conversationId)}/share`, {
      method: 'DELETE',
    });
  }

  getPublicSnapshot(token: string): Promise<PublicSnapshotResponse> {
    return this.request<PublicSnapshotResponse>(`/s/${encodeURIComponent(token)}`, { auth: false });
  }

  // ── Folders ─────────────────────────────────────────────────────────
  listFolders(): Promise<{ folders: Folder[] }> {
    return this.request<{ folders: Folder[] }>('/folders');
  }

  createFolder(body: CreateFolderRequest): Promise<{ folder: Folder }> {
    return this.request<{ folder: Folder }>('/folders', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  updateFolder(id: string, patch: UpdateFolderRequest): Promise<{ folder: Folder }> {
    return this.request<{ folder: Folder }>(`/folders/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  deleteFolder(id: string): Promise<void> {
    return this.requestVoid(`/folders/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  createSoulIdentity(body: CreateSoulIdentityRequest): Promise<{ soulIdentity: SoulIdentity }> {
    return this.request<{ soulIdentity: SoulIdentity }>('/soul-identities', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  listSoulIdentities(): Promise<ListSoulIdentitiesResponse> {
    return this.request<ListSoulIdentitiesResponse>('/soul-identities');
  }

  getSoulIdentity(id: string): Promise<{ soulIdentity: SoulIdentity }> {
    return this.request<{ soulIdentity: SoulIdentity }>(
      `/soul-identities/${encodeURIComponent(id)}`,
    );
  }

  /** Starts building the 3D character of a ready Soul ID (a paid background job). */
  startSoulModel3d(id: string): Promise<{ soulIdentity: SoulIdentity; job: Job }> {
    return this.request<{ soulIdentity: SoulIdentity; job: Job }>(
      `/soul-identities/${encodeURIComponent(id)}/model-3d`,
      { method: 'POST' },
    );
  }

  createCharacter(body: CreateCharacterRequest): Promise<{ character: Character }> {
    return this.request<{ character: Character }>('/characters', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  listCharacters(): Promise<ListCharactersResponse> {
    return this.request<ListCharactersResponse>('/characters');
  }

  updateCharacter(id: string, patch: UpdateCharacterRequest): Promise<{ character: Character }> {
    return this.request<{ character: Character }>(`/characters/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  deleteCharacter(id: string): Promise<void> {
    return this.requestVoid(`/characters/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  createAiInfluencer(body: CreateAiInfluencerRequest): Promise<{ aiInfluencer: AiInfluencer }> {
    return this.request<{ aiInfluencer: AiInfluencer }>('/ai-influencers', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  listAiInfluencers(): Promise<ListAiInfluencersResponse> {
    return this.request<ListAiInfluencersResponse>('/ai-influencers');
  }

  updateAiInfluencer(
    id: string,
    patch: UpdateAiInfluencerRequest,
  ): Promise<{ aiInfluencer: AiInfluencer }> {
    return this.request<{ aiInfluencer: AiInfluencer }>(
      `/ai-influencers/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(patch),
      },
    );
  }

  deleteAiInfluencer(id: string): Promise<void> {
    return this.requestVoid(`/ai-influencers/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async createSoulCast(body: CreateSoulCastRequest): Promise<CreateSoulCastResponse> {
    return this.request<CreateSoulCastResponse>('/soul-cast', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** Worst-case price of a Soul Cast before it is started. */
  estimateSoulCast(body: SoulCastEstimateRequest): Promise<SoulCastEstimateResponse> {
    return this.request<SoulCastEstimateResponse>('/soul-cast/estimate', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** "Photo coach": what a vision model thinks of the photo set before training. */
  checkSoulPhotos(photoUrls: string[]): Promise<SoulPhotoCheckResponse> {
    return this.request<SoulPhotoCheckResponse>('/soul-identities/photo-check', {
      method: 'POST',
      body: JSON.stringify({ photoUrls }),
    });
  }

  async rateJobLikeness(id: string, rating: JobLikenessRating | null): Promise<Job> {
    const response = await this.request<{ job: Job }>(`/jobs/${encodeURIComponent(id)}/rating`, {
      method: 'PATCH',
      body: JSON.stringify({ rating }),
    });
    return this.withMediaUrls(response.job);
  }

  setFeedback(messageId: string, feedback: 'up' | 'down' | null): Promise<void> {
    return this.requestVoid(`/messages/${encodeURIComponent(messageId)}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ feedback }),
    });
  }

  // ── Projects ────────────────────────────────────────────────────────
  listProjects(): Promise<ProjectListResponse> {
    return this.request<ProjectListResponse>('/projects');
  }

  createProject(body: CreateProjectRequest): Promise<Project> {
    return this.request<Project>('/projects', { method: 'POST', body: JSON.stringify(body) });
  }

  updateProject(id: string, patch: Partial<CreateProjectRequest>): Promise<Project> {
    return this.request<Project>(`/projects/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  deleteProject(id: string): Promise<void> {
    return this.requestVoid(`/projects/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  // ── Billing ─────────────────────────────────────────────────────────
  getTransactions(limit = 20, offset = 0): Promise<TransactionListResponse> {
    return this.request<TransactionListResponse>(
      `/billing/transactions?limit=${limit}&offset=${offset}`,
    );
  }

  topup(amountKopecks: number): Promise<TopupResponse> {
    return this.request<TopupResponse>('/billing/topup', {
      method: 'POST',
      body: JSON.stringify({ amountKopecks }),
    });
  }

  // ── Jobs ─────────────────────────────────────────────────────────────
  async listJobs(
    options: { limit?: number; cursor?: string | null } = {},
  ): Promise<JobListResponse> {
    const params = new URLSearchParams();
    if (options.limit) params.set('limit', String(options.limit));
    if (options.cursor) params.set('cursor', options.cursor);
    const suffix = params.toString();
    const page = await this.request<JobListResponse>(`/jobs${suffix ? `?${suffix}` : ''}`);
    return { ...page, jobs: page.jobs.map((job) => this.withMediaUrls(job)) };
  }

  async createJob(body: CreateJobRequest): Promise<Job> {
    const response = await this.request<{ job: Job }>('/jobs', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return this.withMediaUrls(response.job);
  }

  // ── Live voice conversation ────────────────────────────────────────
  transcribeVoice(body: VoiceTranscribeRequest): Promise<VoiceTranscribeResponse> {
    return this.request<VoiceTranscribeResponse>('/voice/transcribe', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  speakVoice(body: VoiceSpeakRequest): Promise<VoiceSpeakResponse> {
    return this.request<VoiceSpeakResponse>('/voice/speak', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** Stores a finished photo/video as a chat attachment, ready to send to a friend or group. */
  createJobAttachment(id: string, index = 0): Promise<JobAttachmentResponse> {
    return this.request<JobAttachmentResponse>(`/jobs/${encodeURIComponent(id)}/attachment`, {
      method: 'POST',
      body: JSON.stringify({ index }),
    });
  }

  /** A finished result as a file, so it can be reused as a reference in another section. */
  getJobMedia(id: string, index = 0): Promise<JobMediaResponse> {
    return this.request<JobMediaResponse>(`/jobs/${encodeURIComponent(id)}/media?index=${index}`);
  }

  async toggleJobFavorite(id: string, isFavorite: boolean): Promise<Job> {
    const response = await this.request<{ job: Job }>(`/jobs/${encodeURIComponent(id)}/favorite`, {
      method: 'PATCH',
      body: JSON.stringify({ isFavorite }),
    });
    return this.withMediaUrls(response.job);
  }

  deleteJob(id: string): Promise<void> {
    return this.requestVoid(`/jobs/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  // ── Admin ───────────────────────────────────────────────────────────
  adminStats(): Promise<AdminStatsResponse> {
    return this.request<AdminStatsResponse>('/admin/stats');
  }

  adminSettings(): Promise<AdminSettingsResponse> {
    return this.request<AdminSettingsResponse>('/admin/settings');
  }

  adminUpdateSettings(patch: AdminSettingsUpdate): Promise<AdminSettingsResponse> {
    return this.request<AdminSettingsResponse>('/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  adminUsers(): Promise<AdminUsersResponse> {
    return this.request<AdminUsersResponse>('/admin/users');
  }

  adminBanUser(userId: string, banned: boolean): Promise<void> {
    return this.requestVoid(`/admin/users/${encodeURIComponent(userId)}/ban`, {
      method: 'POST',
      body: JSON.stringify({ banned }),
    });
  }

  adminAdjustBalance(body: AdminBalanceAdjustRequest): Promise<void> {
    return this.requestVoid('/admin/balance', { method: 'POST', body: JSON.stringify(body) });
  }

  adminUpdateModelPrice(modelId: string, price: Partial<ModelPrice>): Promise<AIModel> {
    return this.request<AIModel>(`/admin/models/${encodeURIComponent(modelId)}/price`, {
      method: 'PATCH',
      body: JSON.stringify({ price }),
    });
  }

  adminModelsMonitoring(
    query: AdminModelsMonitoringQuery = {},
  ): Promise<AdminModelsMonitoringResponse> {
    const search = new URLSearchParams();
    if (query.provider) search.set('provider', query.provider);
    if (query.status) search.set('status', query.status);
    if (query.modelId) search.set('modelId', query.modelId);
    if (query.eventType) search.set('eventType', query.eventType);
    if (query.eventSeverity) search.set('eventSeverity', query.eventSeverity);
    if (query.eventStatus) search.set('eventStatus', query.eventStatus);
    if (query.search) search.set('search', query.search);
    if (query.from) search.set('from', query.from);
    if (query.to) search.set('to', query.to);
    if (query.limit !== undefined) search.set('limit', String(query.limit));
    if (query.offset !== undefined) search.set('offset', String(query.offset));
    const suffix = search.toString();
    return this.request<AdminModelsMonitoringResponse>(
      `/admin/models${suffix ? `?${suffix}` : ''}`,
    );
  }

  adminSyncModels(): Promise<{ reports: Array<Record<string, unknown>> }> {
    return this.request<{ reports: Array<Record<string, unknown>> }>('/admin/models/sync', {
      method: 'POST',
    });
  }

  adminCheckModel(modelId: string): Promise<{ model: AIModel }> {
    return this.request<{ model: AIModel }>(`/admin/models/${encodeURIComponent(modelId)}/check`, {
      method: 'POST',
    });
  }

  adminSetModelFlags(
    modelId: string,
    flags: { isActive?: boolean; isAvailable?: boolean; availabilityStatus?: string },
  ): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/admin/models/${encodeURIComponent(modelId)}/flags`, {
      method: 'PATCH',
      body: JSON.stringify(flags),
    });
  }

  adminSubscriptionPlans(): Promise<AdminSubscriptionPlansResponse> {
    return this.request<AdminSubscriptionPlansResponse>('/admin/subscriptions/plans');
  }

  adminUpdateSubscriptionPlan(
    priceRub: 99 | 490 | 1990 | 4990,
    body: AdminUpdateSubscriptionPlanRequest,
  ): Promise<{ plan: AdminSubscriptionPlansResponse['plans'][number] }> {
    return this.request<{ plan: AdminSubscriptionPlansResponse['plans'][number] }>(
      `/admin/subscriptions/plans/${encodeURIComponent(String(priceRub))}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    );
  }

  adminGrantSubscription(
    body: AdminGrantSubscriptionRequest,
  ): Promise<AdminGrantSubscriptionResponse> {
    return this.request<AdminGrantSubscriptionResponse>('/admin/subscriptions/grants', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  adminPaymentProviders(): Promise<AdminPaymentProvidersResponse> {
    return this.request<AdminPaymentProvidersResponse>('/admin/payments/providers');
  }

  adminCreateCheckout(body: AdminCreateCheckoutRequest): Promise<AdminCreateCheckoutResponse> {
    return this.request<AdminCreateCheckoutResponse>('/admin/payments/checkout', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  adminListErrors(
    params: {
      source?: 'api' | 'web' | 'job';
      severity?: 'error' | 'warning' | 'info';
      code?: string;
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<AdminErrorLogsResponse> {
    const query = new URLSearchParams();
    if (params.source) query.set('source', params.source);
    if (params.severity) query.set('severity', params.severity);
    if (params.code) query.set('code', params.code);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.offset !== undefined) query.set('offset', String(params.offset));
    const suffix = query.toString();
    return this.request<AdminErrorLogsResponse>(`/admin/errors${suffix ? `?${suffix}` : ''}`);
  }

  adminAnalyticsUsers(
    params: AdminAnalyticsUsersRequest = {},
    signal?: AbortSignal,
  ): Promise<AdminAnalyticsUsersResponse> {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.offset !== undefined) query.set('offset', String(params.offset));
    const suffix = query.toString();
    return this.request<AdminAnalyticsUsersResponse>(
      `/admin/analytics/users${suffix ? `?${suffix}` : ''}`,
      { signal },
    );
  }

  adminAnalyticsGateway(
    body: AdminAnalyticsGatewayRequest,
    signal?: AbortSignal,
  ): Promise<AdminAnalyticsGatewayResponse> {
    return this.request<AdminAnalyticsGatewayResponse>('/admin/analytics/gateway', {
      method: 'POST',
      body: JSON.stringify(body),
      signal,
    });
  }

  adminUserDevices(userId: string): Promise<AdminUserDevicesResponse> {
    return this.request<AdminUserDevicesResponse>(
      `/admin/analytics/users/${encodeURIComponent(userId)}/devices`,
    );
  }

  adminUserIpAddresses(userId: string): Promise<AdminUserIpAddressesResponse> {
    return this.request<AdminUserIpAddressesResponse>(
      `/admin/analytics/users/${encodeURIComponent(userId)}/ip-addresses`,
    );
  }

  adminUserAnalyticsSummary(userId: string): Promise<AdminUserAnalyticsSummaryResponse> {
    return this.request<AdminUserAnalyticsSummaryResponse>(
      `/admin/analytics/users/${encodeURIComponent(userId)}/summary`,
    );
  }

  adminUserAnalytics(userId: string): Promise<AdminUserAnalyticsResponse> {
    return this.request<AdminUserAnalyticsResponse>(
      `/admin/analytics/users/${encodeURIComponent(userId)}/sessions`,
    );
  }

  adminUserPageViews(userId: string): Promise<AdminUserPageViewsResponse> {
    return this.request<AdminUserPageViewsResponse>(
      `/admin/analytics/users/${encodeURIComponent(userId)}/page-views`,
    );
  }

  adminUserSessionEvents(userId: string): Promise<AdminUserSessionEventsResponse> {
    return this.request<AdminUserSessionEventsResponse>(
      `/admin/analytics/users/${encodeURIComponent(userId)}/session-events`,
    );
  }

  adminUserModelClicks(userId: string): Promise<AdminModelClickEventsResponse> {
    return this.request<AdminModelClickEventsResponse>(
      `/admin/analytics/users/${encodeURIComponent(userId)}/model-clicks`,
    );
  }

  adminUserErrorLogs(userId: string): Promise<AdminUserErrorLogsResponse> {
    return this.request<AdminUserErrorLogsResponse>(
      `/admin/analytics/users/${encodeURIComponent(userId)}/errors`,
    );
  }

  adminTopModels(
    params: { from?: string; to?: string; limit?: number } = {},
  ): Promise<AdminTopModelsResponse> {
    const query = new URLSearchParams();
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    const suffix = query.toString();
    return this.request<AdminTopModelsResponse>(
      `/admin/analytics/top-models${suffix ? `?${suffix}` : ''}`,
    );
  }

  adminQueryTable(body: AnalyticsQueryTableRequest): Promise<AnalyticsQueryTableResponse> {
    return this.request<AnalyticsQueryTableResponse>('/admin/analytics/query-table', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  trackBatch(body: TrackBatchRequest, options: { keepalive?: boolean } = {}): Promise<void> {
    return this.requestVoid('/analytics/batch', {
      method: 'POST',
      body: JSON.stringify(body),
      keepalive: options.keepalive,
    });
  }

  trackDevice(body: TrackDeviceRequest): Promise<void> {
    return this.requestVoid('/analytics/device', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  trackPageView(body: TrackPageViewRequest): Promise<void> {
    return this.requestVoid('/analytics/page-view', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  trackSectionClick(body: TrackSectionClickRequest): Promise<void> {
    return this.requestVoid('/analytics/section-click', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  trackModelClick(body: TrackModelClickRequest): Promise<void> {
    return this.requestVoid('/analytics/model-click', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  trackSessionEvent(
    body: TrackSessionEventRequest,
    mode: 'create' | 'update' = 'update',
  ): Promise<void> {
    return this.requestVoid('/analytics/session-event', {
      method: mode === 'create' ? 'POST' : 'PATCH',
      body: JSON.stringify(body),
    });
  }

  reportError(body: ReportErrorRequest): Promise<void> {
    return this.requestVoid('/errors/report', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // ── Internals ───────────────────────────────────────────────────────
  private url(path: string): string {
    return `${this.baseUrl}/api${path}`;
  }

  /**
   * Files kept on the API server come back as `/api/files/<signed token>`; the Mini App
   * is served from another origin, so such paths are pinned to the API host.
   */
  mediaUrl(url: string): string {
    return url.startsWith('/api/') ? `${this.baseUrl}${url}` : url;
  }

  private withMediaUrls(job: Job): Job {
    return {
      ...job,
      resultUrl: job.resultUrl ? this.mediaUrl(job.resultUrl) : job.resultUrl,
      resultUrls: job.resultUrls ? job.resultUrls.map((url) => this.mediaUrl(url)) : job.resultUrls,
      previewUrls: job.previewUrls
        ? job.previewUrls.map((url) => (url ? this.mediaUrl(url) : null))
        : job.previewUrls,
    };
  }

  /**
   * `Content-Type: application/json` is only added when the request actually
   * carries a JSON body. Fastify's JSON body parser rejects an empty body
   * whenever this header is present (FST_ERR_CTP_EMPTY_JSON_BODY), which is
   * exactly what plain DELETE calls (no body) were sending — that's why
   * deleting conversations/folders/projects/the account always failed with
   * a 500 while PATCH (which always has a body) worked fine.
   */
  private authHeaders(auth: boolean, hasBody: boolean): Record<string, string> {
    const headers: Record<string, string> = {};
    if (hasBody) headers['Content-Type'] = 'application/json';
    if (auth && this.accessToken) {
      headers.Authorization = `${TOKEN_SCHEME} ${this.accessToken}`;
    }
    return headers;
  }

  private async request<T>(
    path: string,
    init: RequestInit & { auth?: boolean } = {},
    retry = true,
  ): Promise<T> {
    const { auth = true, headers, body, ...rest } = init;
    let response: Response;
    try {
      response = await fetch(this.url(path), {
        ...rest,
        body,
        headers: {
          ...this.authHeaders(auth, body !== undefined),
          ...(headers as Record<string, string> | undefined),
        },
      });
    } catch (error) {
      throw new ApiError('network', (error as Error).message || 'Network error', 0);
    }
    if (response.status === 401 && retry && auth && (await this.refresh())) {
      return this.request<T>(path, init, false);
    }
    if (!response.ok) throw await this.toError(response);
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  private async requestVoid(
    path: string,
    init: RequestInit & { auth?: boolean } = {},
  ): Promise<void> {
    await this.request<unknown>(path, init);
  }

  private rawXhrStream(body: SendMessageRequest, signal?: AbortSignal): XHRChatStream {
    return new XHRChatStream(this.url('/chat/messages'), {
      method: 'POST',
      headers: { ...this.authHeaders(true, true), Accept: 'text/event-stream' },
      body: JSON.stringify(body),
      signal,
    });
  }

  private async toError(response: Response): Promise<ApiError> {
    let code = 'INTERNAL';
    let message = response.statusText || 'Request failed';
    const requestId = response.headers.get('x-request-id');
    let parsedOk = false;
    try {
      const text = await response.text();
      const body = JSON.parse(text) as Partial<ApiErrorBody>;
      parsedOk = true;
      if (body.error) {
        code = body.error.code ?? code;
        message = body.error.message || message;
      } else {
        console.error('[api] toError: missing body.error', {
          status: response.status,
          url: response.url,
          body: text.slice(0, 500),
        });
      }
    } catch {
      console.error('[api] toError: non-JSON response', {
        status: response.status,
        url: response.url,
      });
    }
    if (!parsedOk) {
      console.error('[api] toError: falling back to status text', {
        status: response.status,
        url: response.url,
        message,
      });
    }
    return new ApiError(code, message, response.status, requestId);
  }

  private errorFromText(status: number, text: string): ApiError {
    if (status === 0) {
      return new ApiError('network', 'Network error', 0);
    }
    let code = 'INTERNAL';
    let message = `Request failed with status ${status}`;
    try {
      const body = JSON.parse(text) as Partial<ApiErrorBody>;
      if (body.error) {
        code = body.error.code ?? code;
        message = body.error.message || message;
      } else {
        console.error('[api] errorFromText: missing body.error', {
          status,
          body: text.slice(0, 500),
        });
      }
    } catch {
      // non-JSON error body — keep the generic message.
    }
    return new ApiError(code, message, status);
  }
}

export function createApiClient(): ApiClient {
  const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
  return new ApiClient(baseUrl);
}
